"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/content";
import { Logo } from "./logo";
import { ThemeToggle } from "./theme-toggle";
import { MagneticButton } from "@/components/ui/magnetic-button";
import { useCalendlyPopup } from "@/components/ui/calendly-popup";
import { Container } from "./container";

export function Nav() {
  const calendlyUrl = process.env.NEXT_PUBLIC_CALENDLY_URL;
  const { open: openCalendly, loading: calendlyLoading } = useCalendlyPopup(calendlyUrl);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Keep the panel in the DOM for the exit transition after `mobileOpen` flips false; unmount once
  // the ~180ms close animation finishes. This is the CSS-transition replacement for framer-motion's
  // AnimatePresence exit — same enter/exit choreography, no dependency.
  const [menuMounted, setMenuMounted] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile menu on route change
  useEffect(() => setMobileOpen(false), [pathname]);

  // Drive mount/unmount from the open flag with an exit delay so the close animation can play.
  useEffect(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (mobileOpen) {
      setMenuMounted(true);
    } else if (menuMounted) {
      setMenuVisible(false); // trigger the exit transition, then unmount after it finishes
      closeTimer.current = setTimeout(() => setMenuMounted(false), 200);
    }
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [mobileOpen, menuMounted]);

  // Once the panel is mounted (in its closed state), flip to visible on the next frame so the CSS
  // enter transition actually plays instead of the panel appearing already-open.
  useEffect(() => {
    if (!menuMounted) return;
    const id = requestAnimationFrame(() => setMenuVisible(true));
    return () => cancelAnimationFrame(id);
  }, [menuMounted]);

  const isActive = (href: string) => (href === "/" ? pathname === "/" : pathname.startsWith(href));

  return (
    <header className="fixed inset-x-0 top-0 z-[var(--z-nav,50)] px-4 pt-4 sm:px-6">
      <div
        className={`relative mx-auto flex max-w-[76rem] items-center justify-between rounded-2xl border transition-[background-color,border-color,backdrop-filter,box-shadow,padding] duration-300 ${
          scrolled
            ? "border-[var(--color-border)] bg-[var(--color-bg-elevated)]/85 px-4 py-2.5 shadow-[0_1px_0_0_rgba(255,255,255,0.06)_inset,0_1px_0_0_rgba(0,0,0,0.2),0_24px_60px_-28px_rgba(120,61,215,0.55)] backdrop-blur-2xl"
            : "border-transparent bg-transparent px-4 py-3.5"
        }`}
      >
        {/* faint top hairline highlight, only visible once the bar has surface to catch it */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-4 top-0 h-px rounded-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-opacity duration-300"
          style={{ opacity: scrolled ? 1 : 0 }}
        />

        <nav aria-label="Primary" className="flex w-full items-center justify-between">
          <Link href="/" aria-label="Exec9 home" className="group relative flex items-center">
            {/* Logo hover-scale is a plain CSS transform transition (was a framer-motion spring) —
                the ease-out curve reads the same for a hover flourish and drops the dependency. */}
            <span className="relative flex items-center transition-transform duration-300 ease-out group-hover:scale-[1.06]">
              <span
                aria-hidden
                className="pointer-events-none absolute -inset-3 rounded-full opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
                style={{ background: "radial-gradient(circle, color-mix(in oklch, var(--color-primary) 45%, transparent) 0%, transparent 70%)" }}
              />
              <Logo className="relative h-7" />
            </span>
          </Link>

          {/* center links: tight, exact — a single glowing dot marks hover/active, no underline sweep */}
          <div
            className="hidden items-center gap-1 lg:flex"
            onMouseLeave={() => setHovered(null)}
          >
            {NAV_LINKS.map((link) => {
              const active = isActive(link.href);
              const isHovered = hovered === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onMouseEnter={() => setHovered(link.href)}
                  className="relative flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[0.8125rem] font-medium transition-colors duration-200"
                  style={{ color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)" }}
                >
                  <span
                    className="h-1 w-1 rounded-full transition-all duration-200"
                    style={{
                      background: active || isHovered ? "var(--color-primary)" : "var(--color-border)",
                      boxShadow: active || isHovered ? "0 0 8px color-mix(in oklch, var(--color-primary) 80%, transparent)" : "none",
                    }}
                    aria-hidden
                  />
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden items-center gap-2.5 lg:flex">
            <ThemeToggle />
            <span className="mx-0.5 h-4 w-px bg-[var(--color-border)]" aria-hidden />
            <MagneticButton
              {...(calendlyUrl ? { onClick: openCalendly, disabled: calendlyLoading } : { href: "/contact" })}
              className="group/cta relative inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3.5 py-1.5 text-[0.8125rem] font-medium text-white shadow-[0_1px_0_0_rgba(255,255,255,0.25)_inset,0_4px_16px_-6px_color-mix(in_oklch,var(--color-primary)_70%,transparent)] transition-[background-color,box-shadow] duration-300 hover:bg-[var(--color-primary-dark)] hover:shadow-[0_1px_0_0_rgba(255,255,255,0.25)_inset,0_6px_22px_-6px_color-mix(in_oklch,var(--color-primary)_85%,transparent)] disabled:opacity-70"
            >
              Book a call
              <span aria-hidden className="text-[0.9em] opacity-70 transition-transform duration-300 group-hover/cta:translate-x-0.5">→</span>
            </MagneticButton>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle />
            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-8 w-8 items-center justify-center text-[var(--color-text-primary)]"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </div>

      {menuMounted && (
        <div
          className="mx-auto mt-2 max-w-[76rem] origin-top overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-elevated)]/95 backdrop-blur-2xl transition-[opacity,transform] duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)] lg:hidden"
          style={{
            opacity: menuVisible ? 1 : 0,
            transform: menuVisible ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.98)",
          }}
        >
            <div className="flex flex-col gap-0.5 p-3">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-[0.9375rem] transition-colors"
                  style={{
                    color: isActive(link.href) ? "var(--color-primary-text)" : "var(--color-text-secondary)",
                    background: isActive(link.href) ? "var(--color-bg)" : "transparent",
                  }}
                >
                  {link.label}
                </Link>
              ))}
              {calendlyUrl ? (
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openCalendly();
                  }}
                  className="mt-2 rounded-lg bg-[var(--color-primary)] px-3.5 py-2.5 text-center text-[0.9375rem] font-medium text-white"
                >
                  Book a call
                </button>
              ) : (
                <Link
                  href="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 rounded-lg bg-[var(--color-primary)] px-3.5 py-2.5 text-center text-[0.9375rem] font-medium text-white"
                >
                  Let&apos;s talk
                </Link>
              )}
            </div>
        </div>
      )}
    </header>
  );
}
